import { CloudinaryImage } from "@cloudinary/url-gen";
import { format } from "@cloudinary/url-gen/actions/delivery";
import { fill } from "@cloudinary/url-gen/actions/resize";
// eslint-disable-next-line import/default
import type ICloudConfig from "@cloudinary/url-gen/config/interfaces/Config/ICloudConfig";
import { auto } from "@cloudinary/url-gen/qualifiers/format";
import type { ImageLoaderProps } from "next/image";

const cloudName =
  process.env.NEXT_PUBLIC_CLOUDINARY_CLOUDNAME ?? "beautypie-preview";
const apiKey = process.env.NEXT_PUBLIC_CLOUDINARY_API_KEY;
const apiSecret = process.env.NEXT_PUBLIC_CLOUDINARY_API_SECRET;

const options: ICloudConfig = {
  cloudName,
  apiKey,
  apiSecret
};

/**
 * Beautypie domains
 *
 * Images on these domains are 'public' folder assets
 */
const bpDomainRegex =
  /^https?:\/\/(www\.)?beautypie(-preview|-sandbox)?\.com(\/|$)/;
const featureDomainRegex =
  /^(https?:\/\/)?(www\.)?[a-z0-9]+\.frontend\.features\.beautypie-preview\.com(\/|$)/;

/**
 * Local assets
 */
const localImageRegex = /^\/?images\/.*\..*$/;
const nextStaticRegex = /\/?_next\/static\/.*\..*$/;

/**
 * Mapping Domains
 *
 * These urls are mapped to upload presets in Cloudinary.  This shortens the generated
 * url significantly mitigtaing the risk of hitting the 8k character limit
 *
 * @see https://cloudinary.com/documentation/fetch_remote_images#auto_upload_remote_files
 */

const bynderOldHostingDomain =
  "https://d2csxpduxe849s.cloudfront.net/media/192DFBC7-8953-4970-B7753853A910CFA9";
const bynderNewHostingDomain = "https://beautypie.bynder.com";
const beautypieDAMDomain = "https://brand.beautypie.com";

export const cloudinaryLoader = ({
  src,
  width,
  quality
}: ImageLoaderProps): string => {
  // All local images should be servered as is
  if (
    [localImageRegex, nextStaticRegex, bpDomainRegex, featureDomainRegex].some(
      (regex) => regex.test(src)
    )
  ) {
    return src;
  }

  // create Cloudinary image instance
  const image = new CloudinaryImage(src, options)
    .resize(fill().width(width))
    .quality(quality ?? "auto")
    .delivery(format(auto()));

  // Map old Bynder domain assets to 'bynder-cf' folder
  if (src.startsWith(bynderOldHostingDomain)) {
    return image
      .setPublicID(src.replace(bynderOldHostingDomain, "bynder-cf"))
      .toURL();
  }

  // Map new Bynder domain assets to 'bynder' folder
  if (src.startsWith(bynderNewHostingDomain)) {
    return image
      .setPublicID(src.replace(bynderNewHostingDomain, "bynder"))
      .toURL();
  }

  // Map new Bynder DAM assets to 'bynder-brand' folder
  if (src.startsWith(beautypieDAMDomain)) {
    return image
      .setPublicID(src.replace(beautypieDAMDomain, "bynder-brand"))
      .toURL();
  }

  // All other assets are fetched through Cloudinary
  return image.setPublicID(src).setDeliveryType("fetch").toURL();
};
